/**
 * Represents the internal tag class type used to identify how a PDF or ASN.1 tag
 * is categorized within the encoding structure.
 *
 * @private
 */
export var _TagClassType;
(function (_TagClassType) {
    _TagClassType[_TagClassType["universal"] = 0] = "universal";
    _TagClassType[_TagClassType["application"] = 1] = "application";
    _TagClassType[_TagClassType["context"] = 2] = "context";
    _TagClassType[_TagClassType["abstractSyntaxPrivate"] = 3] = "abstractSyntaxPrivate";
    _TagClassType[_TagClassType["nullValue"] = 5] = "nullValue";
})(_TagClassType || (_TagClassType = {}));
/**
 * Represents the internal construction type for encoded elements, indicating
 * whether the value is primitive or constructed.
 *
 * @private
 */
export var _ConstructionType;
(function (_ConstructionType) {
    _ConstructionType[_ConstructionType["primitive"] = 0] = "primitive";
    _ConstructionType[_ConstructionType["constructed"] = 1] = "constructed";
})(_ConstructionType || (_ConstructionType = {}));
/**
 * Represents the internal real-number special value types used for identifying
 * infinities, NaN values, and signed zero within encoded real numbers.
 *
 * @private
 */
export var _RealValueType;
(function (_RealValueType) {
    _RealValueType[_RealValueType["plusInfinity"] = 64] = "plusInfinity";
    _RealValueType[_RealValueType["minusInfinity"] = 65] = "minusInfinity";
    _RealValueType[_RealValueType["notANumber"] = 66] = "notANumber";
    _RealValueType[_RealValueType["minusZero"] = 67] = "minusZero";
})(_RealValueType || (_RealValueType = {}));
/**
 * Represents the internal encoding base used for representing real numbers
 * in binary, octal, or hexadecimal form.
 *
 * @private
 */
export var _RealEncodingBase;
(function (_RealEncodingBase) {
    _RealEncodingBase[_RealEncodingBase["base2"] = 0] = "base2";
    _RealEncodingBase[_RealEncodingBase["base8"] = 16] = "base8";
    _RealEncodingBase[_RealEncodingBase["base16"] = 32] = "base16";
})(_RealEncodingBase || (_RealEncodingBase = {}));
/**
 * Represents the internal scaling factor used to modify the exponent of
 * encoded real numbers during interpretation.
 *
 * @private
 */
export var _RealEncodingScale;
(function (_RealEncodingScale) {
    _RealEncodingScale[_RealEncodingScale["scale0"] = 0] = "scale0";
    _RealEncodingScale[_RealEncodingScale["scale1"] = 4] = "scale1";
    _RealEncodingScale[_RealEncodingScale["scale2"] = 8] = "scale2";
    _RealEncodingScale[_RealEncodingScale["scale3"] = 12] = "scale3";
})(_RealEncodingScale || (_RealEncodingScale = {}));
/**
 * Represents the internal universal tag types used for defining the semantics of
 * encoded values within PDF and ASN.1 universal type systems.
 *
 * @private
 */
export var _UniversalType;
(function (_UniversalType) {
    _UniversalType[_UniversalType["endOfContent"] = 0] = "endOfContent";
    _UniversalType[_UniversalType["abstractSyntaxBoolean"] = 1] = "abstractSyntaxBoolean";
    _UniversalType[_UniversalType["integer"] = 2] = "integer";
    _UniversalType[_UniversalType["bitString"] = 3] = "bitString";
    _UniversalType[_UniversalType["octetString"] = 4] = "octetString";
    _UniversalType[_UniversalType["nullValue"] = 5] = "nullValue";
    _UniversalType[_UniversalType["objectIdentifier"] = 6] = "objectIdentifier";
    _UniversalType[_UniversalType["objectDescriptor"] = 7] = "objectDescriptor";
    _UniversalType[_UniversalType["external"] = 8] = "external";
    _UniversalType[_UniversalType["realNumber"] = 9] = "realNumber";
    _UniversalType[_UniversalType["enumerated"] = 10] = "enumerated";
    _UniversalType[_UniversalType["embeddedDataValue"] = 11] = "embeddedDataValue";
    _UniversalType[_UniversalType["utf8String"] = 12] = "utf8String";
    _UniversalType[_UniversalType["relativeObjectIdentifier"] = 13] = "relativeObjectIdentifier";
    _UniversalType[_UniversalType["reservedBit14"] = 14] = "reservedBit14";
    _UniversalType[_UniversalType["time"] = 14] = "time";
    _UniversalType[_UniversalType["reservedBit15"] = 15] = "reservedBit15";
    _UniversalType[_UniversalType["sequence"] = 16] = "sequence";
    _UniversalType[_UniversalType["abstractSyntaxSet"] = 17] = "abstractSyntaxSet";
    _UniversalType[_UniversalType["numericString"] = 18] = "numericString";
    _UniversalType[_UniversalType["printableString"] = 19] = "printableString";
    _UniversalType[_UniversalType["teleprinterTextExchange"] = 20] = "teleprinterTextExchange";
    _UniversalType[_UniversalType["videoTextInformationSystem"] = 21] = "videoTextInformationSystem";
    _UniversalType[_UniversalType["internationalAlphabetString"] = 22] = "internationalAlphabetString";
    _UniversalType[_UniversalType["universalTime"] = 23] = "universalTime";
    _UniversalType[_UniversalType["generalizedTime"] = 24] = "generalizedTime";
    _UniversalType[_UniversalType["graphicString"] = 25] = "graphicString";
    _UniversalType[_UniversalType["visibleString"] = 26] = "visibleString";
    _UniversalType[_UniversalType["generalString"] = 27] = "generalString";
    _UniversalType[_UniversalType["universalString"] = 28] = "universalString";
    _UniversalType[_UniversalType["characterString"] = 29] = "characterString";
    _UniversalType[_UniversalType["bmpString"] = 30] = "bmpString";
    _UniversalType[_UniversalType["date"] = 31] = "date";
    _UniversalType[_UniversalType["timeOfDay"] = 32] = "timeOfDay";
    _UniversalType[_UniversalType["dateTime"] = 33] = "dateTime";
    _UniversalType[_UniversalType["duration"] = 34] = "duration";
    _UniversalType[_UniversalType["objectIdResourceIdentifier"] = 35] = "objectIdResourceIdentifier";
    _UniversalType[_UniversalType["relativeResourceIdentifier"] = 36] = "relativeResourceIdentifier";
})(_UniversalType || (_UniversalType = {}));
